import { gql, useMutation } from "@apollo/client";
import { Button, MenuItem, TextField } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { isEqual, sortBy, startCase } from "lodash";
import { useMemo, useState } from "react";

import CheckboxSelect from "~/components/common/forms/CheckboxSelect";
import Help from "~/components/common/Help";
import { ErrandType, useOrgConfig } from "~/providers/OrgConfigProvider";
import { durationOptions } from "./VisitResourceRequirementFormRow";

type Props = {
  errandType: ErrandType;
};

const ErrandProviderRequirementForm = ({ errandType }: Props) => {
  const requirement = errandType.visitResourceRequirements[0];
  const base = useMemo(() => ({
    duration: requirement?.duration ?? 15,
    providerTypeIds: requirement?.providerTypes.map(providerType => providerType.id) ?? [],
  }), [requirement]);
  const [local, setLocal] = useState(base);
  const { providerTypes } = useOrgConfig();
  const [create, createState] = useMutation(CREATE_ERRAND_RESOURCE_REQUIREMENT);
  const [update, updateState] = useMutation(UPDATE_ERRAND_RESOURCE_REQUIREMENT);
  const loading = createState.loading || updateState.loading;

  const onSave = async () => {
    const mutation = requirement ? update : create;
    await mutation({
      variables: requirement
        ? { id: requirement.id, ...local }
        : { errandTypeId: errandType.id, ...local },
      awaitRefetchQueries: true,
      refetchQueries: ["OrgConfig"],
    });
  };

  return (
    <Grid container alignItems="center" rowSpacing={2}>
      <Grid size={{ xs: 12, md: 5, lg: 4 }}>
        Provider Types
        <Help text="Which provider types can complete this errand." />
      </Grid>
      <Grid size={{ xs: 12, md: 7, lg: 8 }}>
        <CheckboxSelect
          value={local.providerTypeIds}
          onChange={providerTypeIds => setLocal({ ...local, providerTypeIds })}
          options={sortBy(
            Object.values(providerTypes)
              .flatMap(providerType => providerType
                ? [{ key: providerType.id, label: startCase(providerType.name) }]
                : []),
            "label",
          )}
          aria-label="Provider Types"
          sx={{ width: "90%" }}
          required
        />
      </Grid>
      <Grid size={{ xs: 12, md: 5, lg: 4 }}>
        Duration
        <Help text="How long the provider will spend on the errand." />
      </Grid>
      <Grid size={{ xs: 12, md: 7, lg: 8 }}>
        <TextField
          select
          size="small"
          value={local.duration}
          onChange={event => setLocal({ ...local, duration: Number(event.target.value) })}
          slotProps={{ htmlInput: { "data-testid": "errand-duration-dropdown" } }}
          sx={{ width: "8em" }}
        >
          {durationOptions.map(([value, label]) => (
            <MenuItem key={value} value={value}>{label}</MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid size={12}>
        <Button
          variant="contained"
          size="small"
          disabled={local.providerTypeIds.length === 0 || isEqual(local, base)}
          loading={loading}
          onClick={onSave}
        >
          Save
        </Button>
        <Button
          variant="outlined"
          size="small"
          color="secondary"
          sx={{ ml: 1 }}
          onClick={() => setLocal(base)}
        >
          Reset
        </Button>
      </Grid>
    </Grid>
  );
};

export default ErrandProviderRequirementForm;

const CREATE_ERRAND_RESOURCE_REQUIREMENT = gql(`
  mutation CreateErrandResourceRequirement(
    $errandTypeId: Int!
    $duration: Int!
    $providerTypeIds: [Int!]!
  )
  @permission(requires: manageOrgConfig)
  {
    createVisitResourceRequirement(
      errandTypeId: $errandTypeId
      duration: $duration
      providerTypeIds: $providerTypeIds
    ) {
      id
    }
  }
`);

const UPDATE_ERRAND_RESOURCE_REQUIREMENT = gql(`
  mutation UpdateErrandResourceRequirement(
    $id: Int!
    $duration: Int!
    $providerTypeIds: [Int!]!
  )
  @permission(requires: manageOrgConfig)
  {
    updateVisitResourceRequirement(
      id: $id
      duration: $duration
      providerTypeIds: $providerTypeIds
    ) {
      id
    }
  }
`);
